import React from "react";
import { Link } from "react-router-dom";
import { CookiePreferencesLink } from "../ConsentPreferences";
import AppDownload from "../AppDownload";
import Reveal from "./Reveal";
import Doodle from "./Doodle";
import Highlighter from "./Highlighter";
import logoSrc from "../../letsloop-logo.png";

// Act 6 of the story site: the ask, and the wordmark footer.
//
// The journey has been building to one action since the hero, so this is the
// only place with a single CTA and nothing competing with it.
//
// The oversized wordmark is PLY's closing move (their giant "PLY" on deep
// purple), and it is the right borrow: it ends the page on the brand rather
// than on a link farm. It uses the REAL logo asset scaled up, never type set to
// imitate it, per the standing brand rule. The infinity mark reads as the
// double-o at that size, which is the whole reason it works.
//
// Reuses <AppDownload /> rather than hand-rolling store badges, so the store
// URLs stay in one place and the launch flag that governs them keeps working.

/** Legal link sized for the teal ground. Muted rather than salmon so the strip
 *  stays quiet under the wordmark instead of competing with the store CTAs. */
const FooterLink: React.FC<{ to: string; children: React.ReactNode }> = ({
  to,
  children,
}) => (
  <Link
    to={to}
    className="rounded text-[#f9f9f7]/70 transition-colors hover:text-salmon focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon"
  >
    {children}
  </Link>
);

const DownloadAct: React.FC = () => (
  <section
    id="get-the-app"
    className="relative w-full ground-teal pt-24 md:pt-32 overflow-hidden scroll-mt-4"
  >
    <Doodle
      name="arrow"
      size={70}
      rotate={12}
      className="absolute w-8 h-8 md:w-auto md:h-auto left-[8%] top-[18%] text-salmon/60"
    />
    <Doodle
      name="burst"
      size={42}
      className="absolute w-8 h-8 md:w-auto md:h-auto right-[11%] top-[14%] text-salmon/70"
    />

    <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
      <Reveal y={22}>
        <h2
          className="font-headline text-[#f9f9f7] tracking-[-0.03em] leading-[1.14] text-[clamp(2.2rem,6.4vw,4.2rem)] text-balance"
          style={{ fontWeight: 700 }}
        >
          Something is happening near you{" "}
          <Highlighter tone="coral">this week.</Highlighter>
        </h2>
      </Reveal>
      <Reveal y={14} delay={110}>
        <p className="mt-6 text-lg md:text-xl text-[#f9f9f7]/75 leading-relaxed max-w-xl mx-auto">
          Free to join, real people, and you can be at one of them by Thursday.
        </p>
      </Reveal>
      <Reveal y={16} delay={220} className="mt-10 flex justify-center">
        <AppDownload tone="light" align="center" placement="story" />
      </Reveal>
      <Reveal y={12} delay={300}>
        <p className="mt-8 text-sm text-[#f9f9f7]/60">
          Prefer to look around first?{" "}
          <Link
            to="/how-it-works"
            className="text-salmon font-semibold border-b-2 border-salmon/40 hover:border-salmon transition-colors"
          >
            See how it works
          </Link>
        </p>
      </Reveal>
    </div>

    {/* The wordmark sign-off. Cropped at the baseline so it reads as the page
        ending on the brand rather than as a floating logo. The crop is done by
        this div's own overflow-hidden, so the legal strip below is a sibling
        and does not disturb it. */}
    <div className="mt-20 md:mt-28 px-4 pb-0 overflow-hidden">
      <img
        src={logoSrc}
        alt="LetsLoop"
        className="w-full max-w-6xl mx-auto h-auto opacity-[0.97] translate-y-[8%] select-none"
        loading="lazy"
      />
    </div>

    {/* LEGAL STRIP.
        This teal act is the site's footer on every story-era page, but it had
        no legal layer at all: the shared <Footer> that carries the policy links
        and the statutory disclosure renders only inside the legacy Home
        component, which now lives at /classic. When the story site became "/",
        the homepage, /how-it-works, /communities and /safety all silently lost
        their Terms, Privacy, Cookie-preferences and Delete-account links, and
        the Companies Act disclosure with them.

        The disclosure wording is COPIED VERBATIM from components/Footer.tsx and
        must stay in sync with it — it was verified against the register on
        2026-08-11 (LETSLOOP LTD, 17178512, incorporated 24 April 2026). Do not
        edit either copy without re-checking Companies House.

        Cookie preferences is the shared control, not a link: the consent banner
        is live, so a visitor must be able to reopen and change their choice
        from any page. */}
    <div className="relative z-10 border-t border-[#f9f9f7]/15 px-6 pb-12 pt-8 md:pb-14">
      <div className="mx-auto max-w-4xl">
        <nav
          aria-label="Legal"
          className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm font-medium"
        >
          <FooterLink to="/terms">Terms</FooterLink>
          <FooterLink to="/privacy">Privacy</FooterLink>
          <FooterLink to="/cookies">Cookies</FooterLink>
          <CookiePreferencesLink className="rounded text-[#f9f9f7]/70 transition-colors hover:text-salmon focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon">
            Cookie preferences
          </CookiePreferencesLink>
          <FooterLink to="/safety">Safety</FooterLink>
          <FooterLink to="/support">Support</FooterLink>
          <FooterLink to="/about">About</FooterLink>
          <FooterLink to="/delete-account">Delete account</FooterLink>
        </nav>

        <p className="mx-auto mt-6 max-w-2xl text-center text-xs leading-relaxed text-[#f9f9f7]/55">
          LetsLoop is a trading name of LETSLOOP LTD, registered in England and
          Wales, company number 17178512. Registered office: 71-75 Shelton
          Street, Covent Garden, London WC2H 9JQ.
        </p>
        <p className="mt-2 text-center text-xs text-[#f9f9f7]/45">
          &copy; 2026 LetsLoop Ltd.
        </p>
      </div>
    </div>
  </section>
);

export default DownloadAct;
